import {BillingAddress, IOrderModel, Item, Option, ShippingAddress, StorePickup} from './app.types';
import {
  ISubscription,
  SubscriptionApi,
} from '@wix/wixstores-client-storefront-sdk/dist/es/src/apis/SubscriptionApi/SubscriptionApi';
import {ProductType} from '../components/thankYouPage/constants';
import {SubscriptionFrequency} from '@wix/wixstores-client-storefront-sdk/dist/es/src/graphql/queries-schema';
import {ILocaleDisplayNames} from '@wix/wixstores-client-storefront-sdk/dist/es/src/apis/LocaleApi/LocaleApi';
import {AddressTranslationService} from '../domain/services/AddressTranslationService';
import {prepareSubdivision} from '../components/thankYouPage/order.helper';
import {GetSubscriptionQuery} from '@wix/wixstores-client-storefront-sdk/dist/src/graphql/queries-schema';

type SubscriptionOrderModelArgs = {
  subscriptionId: string;
  subscriptionApi: SubscriptionApi;
  addressTranslationService: AddressTranslationService;
};

export class SubscriptionOrderModel implements IOrderModel {
  private readonly subscription: ISubscription;
  private readonly shippingTranslations: ILocaleDisplayNames;
  private readonly billingTranslations: ILocaleDisplayNames;

  public static async createModel({
    subscriptionId,
    subscriptionApi,
    addressTranslationService,
  }: SubscriptionOrderModelArgs): Promise<SubscriptionOrderModel> {
    const gqlSubscription = await subscriptionApi.fetchSubscription({subscriptionId});
    let shippingTranslations;
    let billingTranslations;

    if (gqlSubscription.shippingAddress?.country) {
      shippingTranslations = await addressTranslationService.translate(
        gqlSubscription.shippingAddress.country,
        // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
        prepareSubdivision(gqlSubscription.shippingAddress.subdivision)
      );
    }

    if (gqlSubscription.billingAddress?.country) {
      billingTranslations = await addressTranslationService.translate(
        gqlSubscription.billingAddress.country,
        // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
        prepareSubdivision(gqlSubscription.billingAddress.subdivision)
      );
    }

    return new SubscriptionOrderModel({
      gqlSubscription,
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      shippingTranslations,
      billingTranslations,
    });
  }

  private constructor({
    gqlSubscription,
    shippingTranslations,
    billingTranslations,
  }: {
    gqlSubscription: ISubscription;
    shippingTranslations: ILocaleDisplayNames;
    billingTranslations?: ILocaleDisplayNames;
  }) {
    this.subscription = gqlSubscription;
    this.shippingTranslations = shippingTranslations;
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    this.billingTranslations = billingTranslations;
  }

  public get buyerName(): string {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.buyerInfo.buyerName;
  }

  public get extendedFields(): null {
    return null;
  }

  public get buyerMail(): string {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.buyerInfo.buyerEmail;
  }

  public get buyerId(): string {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.buyerInfo.buyerId;
  }

  public get orderId(): number {
    return 0;
  }

  public get totalPrice(): number {
    return this.subscription.totals.total;
  }

  public get formattedTotalPrice(): string {
    return this.subscription.totals.formattedTotal;
  }

  public get formattedTaxPrice(): string {
    return this.subscription.totals.formattedTax;
  }

  public get formattedShippingPrice(): string {
    return this.subscription.totals.formattedShipping;
  }

  public get formattedSubTotalPrice(): string {
    return this.subscription.totals.formattedSubtotal;
  }

  public get formattedDiscount(): string {
    return this.subscription.totals.formattedDiscount;
  }

  public get formattedTotalGiftCardAmount(): string {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return null;
  }

  public get hasGiftCard(): boolean {
    return false;
  }

  public get totalGiftCardAmount(): number {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return null;
  }

  public get shippingPrice(): number {
    return this.subscription.totals.shipping;
  }

  public get taxPrice(): number {
    return this.subscription.totals.tax;
  }

  public get paymentMethod(): string {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.paymentInfo?.paymentMethod;
  }

  public get paymentStatus(): string {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.paymentInfo?.paymentStatus;
  }

  public get billingAddress(): BillingAddress {
    const {countryName, regionName} = this.billingTranslations || {};
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return {
      ...this.subscription.billingAddress,
      country: countryName,
      subdivision: regionName || '',
    };
  }

  public get shippingAddress(): ShippingAddress {
    const {company, city, addressLine, addressLine2, zipCode, fullName} = this.subscription.shippingAddress || {};
    const {addressLine1} = this.subscription.shippingAddress || {};
    return {
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      company,
      country: this.shippingTranslations?.countryName,
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      subdivision: this.shippingTranslations?.regionName,
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      city,
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      addressLine,
      addressLine1,
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      addressLine2,
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      zipCode,
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      phone: this.subscription.shippingAddress?.phoneNumber,
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      fullName,
    };
  }

  public get deliveryType(): string {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.deliveryInfo?.deliveryType;
  }

  public get deliveryTime(): string {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.deliveryInfo?.deliveryTime;
  }

  public get storePickup(): StorePickup {
    const {title, deliveryAddress, instruction, deliveryTime} = this.subscription.deliveryInfo || {};

    return {
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      title,
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      address: {...deliveryAddress, state: null},
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      instructions: instruction,
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      time: deliveryTime,
    };
  }

  public get couponCode(): string {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.appliedCoupon?.code;
  }

  public get items(): Item[] {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.lineItems.map((lineItem) => ({
      name: lineItem.productDetails.name,
      price: lineItem.chargeDetails.price,
      sku: lineItem.productDetails.sku,
      productId: lineItem.productDetails.productId,
      quantity: lineItem.quantity,
      productType: ProductType.Physical,
      media: {...lineItem.productDetails.mediaItem, url: lineItem.productDetails.mediaItem?.mediaId},
      options: SubscriptionOrderModel.getSubscriptionOptions(lineItem) ?? /* istanbul ignore next */ [],
      formattedPrice: lineItem.chargeDetails.formattedPrice,
      formattedTotal: lineItem.chargeDetails.formattedTotalPrice,
    }));
  }

  /* istanbul ignore next */
  public get lineItems(): Item[] {
    return this.items;
  }

  // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
  private static getSubscriptionOptions(lineItem: GetSubscriptionQuery['subscription']['lineItems'][0]): Option[] {
    const options =
      lineItem.options?.map((op: {option: string; selection: string}) => ({
        title: op.option,
        description: op.selection,
        isFreeText: false,
      })) ?? [];

    const freeTextOptions =
      lineItem.customTextFields
        ?.filter((op: {title: string; value: string}) => !!op.value)
        .map((freeText: {title: string; value: string}) => ({
          title: freeText.title,
          description: freeText.value,
          isFreeText: true,
        })) ?? [];

    return [...options, ...freeTextOptions];
  }

  public get createdDate(): number {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.createdAt;
  }

  public get cartId(): string {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.cartId;
  }

  public get checkoutId(): string {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.checkoutId;
  }

  public get isValid(): boolean {
    return !!this.subscription.id;
  }

  public get subscriptionFrequency(): SubscriptionFrequency {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.frequency;
  }

  public get subscriptionDuration(): number {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.duration;
  }

  public get subscriptionInterval(): number {
    return this.subscription.interval;
  }

  public get subscriptionName(): string {
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    return this.subscription.name;
  }

  public hasShippingCountryNameTranslation(): boolean {
    return !!this.shippingTranslations.countryName;
  }
}
